import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { AMBASSADOR_SOURCE_LOCATION } from '#src/app/shared/constants';
import { normalize } from '#src/app/shared/utils/string';

import type { PublishedPetitionResponse } from './api/createPublishedPetition';
import { fetchSocialStarterAnglePublishData } from './api/socialStarterAngleSourceLocation';

/**
 * Checks whether an AI draft has been edited by comparing the normalized base and current values.
 * This information is passed in the activate_petition tracking event.
 */
export function compareNormalizedStrings(base: string | undefined | null, value: string): boolean | null {
	if (base === undefined || base === null) return null;

	return normalize(base) !== normalize(value);
}

/**
 * Resolves the Social Starter data to publish with. The draft-angle record is the source of truth
 * for both staffing attribution and the matched decision maker. Non-Social-Starter drafts preserve
 * the URL attribution and have no decision maker.
 */
export async function resolvePublishContext({
	angleToken,
	urlSourceLocation,
	utilityContext,
}: {
	angleToken: string | null | undefined;
	urlSourceLocation: string | undefined;
	utilityContext: UtilityContext;
}): Promise<Readonly<{ sourceLocation: string | undefined; decisionMakerId: string | undefined }>> {
	if (!angleToken) return { sourceLocation: urlSourceLocation, decisionMakerId: undefined };

	const publishData = await fetchSocialStarterAnglePublishData({ token: angleToken, utilityContext });
	// A blank stamped code shouldn't happen, but guard it the same as a missing one — the petition
	// service validates source_location against a fixed set, so an empty string would 400 rather
	// than silently falling back to the URL value.
	return {
		sourceLocation: publishData?.sourceLocation || urlSourceLocation,
		decisionMakerId: publishData?.decisionMakerId ?? undefined,
	};
}

/**
 * Resolves the path to redirect to after publishing a petition, based on whether it's an
 * ambassador petition.
 *
 * Branches on the resolved `source_location` rather than the petition's `isAmbassadorPetition` field:
 * staffing is applied asynchronously, so that field can still read false here — and that false would
 * then be cached for 30 minutes.
 */
export function resolvePostPublishPath({
	petition,
	resolvedSourceLocation,
	isAmbassadorPostCreateFlowEnabled,
}: {
	petition: Pick<PublishedPetitionResponse, 'id' | 'slug'>;
	resolvedSourceLocation: string | undefined;
	isAmbassadorPostCreateFlowEnabled: boolean;
}): string {
	const petitionPath = `/p/${petition.slug ?? petition.id}`;
	const isAmbassadorPetition = resolvedSourceLocation === AMBASSADOR_SOURCE_LOCATION;

	if (isAmbassadorPostCreateFlowEnabled && isAmbassadorPetition) return `${petitionPath}/ambassador-onboarding`;

	return `${petitionPath}/starter-onboarding/welcome`;
}
